import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "../lib/api";
import { AppShell, PageHeader } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { money, centsToInput, fmtDateAU, parseMoneyToCents } from "../lib/format";
import { ReconBadge, PaidBadge, isPaid, RECON_META, RECON_ORDER } from "../components/ReconBadge";
import { toast } from "sonner";
import { Pencil, Trash2, ChevronDown, ChevronUp, Building2, CheckCircle2, RotateCcw } from "lucide-react";

const GST_LABEL = {
  not_registered: "Not GST registered", none: "No GST",
  inclusive: "GST inclusive", plus_gst: "Plus GST (10% added)",
};

const signed = (c) => (c == null ? "—" : (c > 0 ? "+" : "") + money(c));

function Row({ label, value, strong, sub }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className={`text-slate-600 ${strong ? "font-semibold text-slate-900" : ""}`}>{label}{sub && <span className="block text-[11px] text-slate-400 font-normal">{sub}</span>}</span>
      <span className={`font-num text-right ${strong ? "font-bold text-slate-900" : "text-slate-700"}`}>{value}</span>
    </div>
  );
}

export default function PaymentReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: r, refetch, isLoading } = useQuery({
    queryKey: ["payment-report", id],
    queryFn: async () => (await api.get(`/payment-reports/${id}`)).data,
  });

  const [showCalc, setShowCalc] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [actualOpen, setActualOpen] = useState(false);
  const [actualVal, setActualVal] = useState("");
  const [payDate, setPayDate] = useState("");
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paidOpen, setPaidOpen] = useState(false);
  const [unpaidOpen, setUnpaidOpen] = useState(false);

  if (isLoading || !r) {
    return <AppShell><PageHeader title="Payment Report" onBack={() => navigate("/money")} /><div className="px-5 text-slate-400">Loading…</div></AppShell>;
  }

  const paid = isPaid(r);
  const todayISO = new Date().toISOString().slice(0, 10);
  const notesVal = notes === null ? (r.reconciliation_notes || "") : notes;
  const feeCents = r.service_fee_cents != null ? r.service_fee_cents : Math.max((r.adjusted_billing_cents || 0) - (r.expected_cents || 0), 0);
  const feeBps = r.service_fee_bps || 0;
  const dentistPct = r.service_fee_basis === "dentist_share" ? feeBps / 100 : (10000 - feeBps) / 100;
  const practicePct = 100 - dentistPct;

  const invalidate = () => { refetch(); qc.invalidateQueries({ queryKey: ["payment-reports"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const openActual = () => {
    setActualVal(r.actual_cents == null ? "" : centsToInput(r.actual_cents));
    setPayDate(r.payment_date || "");
    setActualOpen(true);
  };

  const saveActual = async () => {
    setBusy(true);
    try {
      await api.put(`/payment-reports/${id}`, {
        practice_id: r.practice_id, period_start: r.period_start, period_end: r.period_end,
        report_date: r.report_date, payment_date: payDate || null,
        invoice_reference: r.invoice_reference || null,
        total_billing_cents: r.total_billing_cents,
        deductions: (r.deductions || []).map((d) => ({ category: d.category, label: d.label, amount_cents: d.amount_cents })),
        actual_cents: actualVal === "" ? null : parseMoneyToCents(actualVal),
        notes: r.notes,
      });
      setActualOpen(false);
      toast.success("Actual payment updated");
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/payment-reports/${id}/reconcile`, { reconciliation_status: status });
      toast.success(`Marked ${RECON_META[status].label}`);
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.post(`/payment-reports/${id}/reconcile`, { reconciliation_notes: notesVal });
      toast.success("Reconciliation notes saved");
      setNotes(null);
      invalidate();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/payment-reports/${id}`);
      setDelOpen(false);
      toast.success("Payment report deleted");
      qc.invalidateQueries();
      navigate("/money");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const putReport = (extra) => api.put(`/payment-reports/${id}`, {
    practice_id: r.practice_id, period_start: r.period_start, period_end: r.period_end,
    report_date: r.report_date,
    invoice_reference: r.invoice_reference || null,
    total_billing_cents: r.total_billing_cents,
    deductions: (r.deductions || []).map((d) => ({ category: d.category, label: d.label, amount_cents: d.amount_cents })),
    notes: r.notes, ...extra,
  });

  const openMarkPaid = () => {
    setActualVal(centsToInput(r.expected_cents || 0));
    setPayDate(r.payment_date || todayISO);
    setPaidOpen(true);
  };
  const confirmPaid = async () => {
    setBusy(true);
    try {
      await putReport({ payment_date: payDate || todayISO,
        actual_cents: actualVal === "" ? (r.expected_cents || 0) : parseMoneyToCents(actualVal) });
      setPaidOpen(false); toast.success("Marked as paid"); invalidate();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const confirmUnpaid = async () => {
    setBusy(true);
    try {
      await putReport({ payment_date: null, actual_cents: null,
        reconciliation_status: "awaiting", reconciliation_notes: "" });
      setUnpaidOpen(false); toast.success("Marked as unpaid"); invalidate();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <AppShell>
      <PageHeader title="Payment Report" subtitle={r.practice_name} onBack={() => navigate("/money")}
        right={
          <div className="flex gap-2">
            <button data-testid="report-edit-button" onClick={() => navigate(`/money/report/${id}/edit`)}
              className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#0D786A] df-pressable"><Pencil size={17} /></button>
            <button data-testid="report-delete-button" onClick={() => setDelOpen(true)}
              className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center text-red-500 df-pressable"><Trash2 size={17} /></button>
          </div>
        } />

      <div className="px-5 lg:px-8 pb-24 df-fade-in space-y-4">
        {/* Practice + period */}
        <div className="df-card p-4" data-testid="report-detail-practice">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-full bg-[#E6F4F1] text-[#0D786A] flex items-center justify-center shrink-0"><Building2 size={18} /></span>
            <div className="min-w-0">
              <div className="font-heading font-semibold text-slate-900 truncate">{r.practice_name}</div>
              <div className="text-xs text-slate-500">Reporting period · {fmtDateAU(r.period_start)} – {fmtDateAU(r.period_end)}</div>
              {r.invoice_reference && <div className="text-xs text-slate-500" data-testid="report-detail-invoice">Invoice / ref · {r.invoice_reference}</div>}
            </div>
          </div>
        </div>

        {/* Payment status */}
        <div className="df-card p-4 flex items-center justify-between gap-3" data-testid="report-payment-status-card">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Payment status</div>
            <PaidBadge report={r} testId="report-paid-badge" />
          </div>
          {paid ? (
            <Button data-testid="mark-unpaid-button" variant="outline" onClick={() => setUnpaidOpen(true)}
              className="h-10 rounded-xl shrink-0"><RotateCcw size={15} className="mr-1.5" />Mark as Unpaid</Button>
          ) : (
            <Button data-testid="mark-paid-button" onClick={openMarkPaid}
              className="h-10 rounded-xl bg-[#0D786A] hover:bg-[#0A6055] shrink-0"><CheckCircle2 size={15} className="mr-1.5" />Mark as Paid</Button>
          )}
        </div>

        {/* Report figures */}
        <div className="df-card p-4 space-y-1.5 text-sm" data-testid="report-detail-figures">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Report</div>
          <Row label="Total billing" value={money(r.total_billing_cents)} />
          {(r.deductions || []).map((d, i) => (
            <Row key={i} label={`Less ${d.label || d.category}`} value={"− " + money(d.amount_cents)} />
          ))}
          <Row label="Adjusted billing" value={money(r.adjusted_billing_cents)} strong />
          <div className="pt-1.5 mt-1 border-t border-slate-100" />
          <Row label={`Facilities / service fee`} sub={`${r.service_fee_basis === "dentist_share" ? "Your share basis" : "Practice share basis"} · ${(feeBps / 100)}%`} value={"− " + money(feeCents)} />
          {r.gst_model === "components" ? (
            <>
              {r.service_fee_gst_cents ? <Row label="GST on facilities fee (10%)" value={"− " + money(r.service_fee_gst_cents)} /> : null}
              <Row label="Dentist amount" value={money(r.dentist_net_cents)} strong />
              {r.dentist_gst_cents ? <Row label="Dentist GST (10%)" value={"+ " + money(r.dentist_gst_cents)} /> : null}
              {!r.service_fee_gst_cents && !r.dentist_gst_cents ? <Row label="GST" value="No GST applied" /> : null}
            </>
          ) : (
            <>
              <Row label="GST treatment" value={GST_LABEL[r.gst_treatment] || "—"} />
              {r.gst_cents ? <Row label="GST amount" value={money(r.gst_cents)} /> : null}
            </>
          )}
          <div className="flex items-center justify-between pt-2 mt-1 border-t border-slate-100">
            <span className="font-semibold text-slate-900">Expected payment</span>
            <span className="font-num font-bold text-lg text-[#0D786A]" data-testid="report-detail-expected">{money(r.expected_cents)}</span>
          </div>
        </div>

        {/* How was this calculated */}
        <div className="df-card overflow-hidden">
          <button data-testid="report-how-calculated" onClick={() => setShowCalc((s) => !s)}
            className="w-full flex items-center justify-between p-4 df-pressable">
            <span className="text-sm font-semibold text-slate-900">How was this calculated?</span>
            {showCalc ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
          </button>
          {showCalc && (
            <div className="px-4 pb-4 space-y-2 text-sm text-slate-600" data-testid="report-calc-explain">
              <p><b className="text-slate-900">1.</b> Start with total billing of <b className="font-num">{money(r.total_billing_cents)}</b>.</p>
              {(r.deductions || []).length > 0 && (
                <p><b className="text-slate-900">2.</b> Subtract deductions ({(r.deductions || []).map((d) => `${d.label || d.category} ${money(d.amount_cents)}`).join(", ")}) = adjusted billing of <b className="font-num">{money(r.adjusted_billing_cents)}</b>.</p>
              )}
              {(r.deductions || []).length === 0 && (
                <p><b className="text-slate-900">2.</b> No deductions, so adjusted billing = <b className="font-num">{money(r.adjusted_billing_cents)}</b>.</p>
              )}
              <p><b className="text-slate-900">3.</b> Facilities / service fee is <b className="font-num">{money(feeCents)}</b> ({(feeBps / 100)}% {r.service_fee_basis === "dentist_share" ? "dentist-share basis" : "practice-share basis"}).</p>
              {r.gst_model === "components" ? (
                <>
                  {r.service_fee_gst_cents ? <p><b className="text-slate-900">4.</b> GST on the facilities fee (10%) = <b className="font-num">{money(r.service_fee_gst_cents)}</b>, deducted from your amount.</p> : null}
                  <p><b className="text-slate-900">{r.service_fee_gst_cents ? "5." : "4."}</b> Dentist amount = <b className="font-num">{money(r.dentist_net_cents)}</b>.{r.dentist_gst_cents ? <> Dentist GST (10%) of <b className="font-num">{money(r.dentist_gst_cents)}</b> is added on top.</> : null}</p>
                </>
              ) : (
                <p><b className="text-slate-900">4.</b> Your share = <b className="font-num">{money(r.expected_cents)}</b>. {GST_LABEL[r.gst_treatment] || ""}{r.gst_cents ? ` GST of ${money(r.gst_cents)} applies.` : ""}</p>
              )}
              <p><b className="text-slate-900">Expected payment: <span className="font-num">{money(r.expected_cents)}</span></b></p>
              <p className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">
                Calculated in whole cents (rounding half up) using this report's saved agreement{r.agreement_version ? ` (version ${r.agreement_version})` : ""}.
                Historical reports are not changed by later agreement updates. This is a financial-organisation figure, not tax or accounting advice.
              </p>
            </div>
          )}
        </div>

        {/* Reconciliation */}
        <div className="df-card p-4 space-y-3" data-testid="report-reconciliation">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Reconciliation</span>
            <ReconBadge status={r.reconciliation_status} testId="report-recon-status" />
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[11px] text-slate-400">Expected</div><div className="font-num font-semibold text-slate-900">{money(r.expected_cents)}</div></div>
            <div><div className="text-[11px] text-slate-400">Actual</div><div className="font-num font-semibold text-slate-900" data-testid="report-detail-actual">{r.actual_cents == null ? "—" : money(r.actual_cents)}</div></div>
            <div><div className="text-[11px] text-slate-400">Payment date</div><div className="font-num text-slate-700">{r.payment_date ? fmtDateAU(r.payment_date) : "—"}</div></div>
            <div><div className="text-[11px] text-slate-400">Variance (actual − expected)</div><div className={`font-num font-semibold ${r.variance_cents > 0 ? "text-emerald-700" : r.variance_cents < 0 ? "text-red-600" : "text-slate-900"}`} data-testid="report-detail-variance">{signed(r.variance_cents)}</div></div>
          </div>
          {r.reconciled_at && <div className="text-[11px] text-slate-400">Reviewed on {fmtDateAU(r.reconciled_at)}</div>}

          <Button data-testid="report-update-actual" variant="outline" onClick={openActual} className="w-full h-11 rounded-xl">
            {r.actual_cents == null ? "Record actual payment" : "Update actual payment"}
          </Button>

          <div>
            <div className="text-[11px] text-slate-400 mb-1.5">Status</div>
            <div className="flex flex-wrap gap-2">
              {RECON_ORDER.map((s) => (
                <button key={s} data-testid={`report-status-${s}`} disabled={busy} onClick={() => setStatus(s)}
                  className={`px-3 h-9 rounded-xl text-xs font-medium border df-pressable ${
                    r.reconciliation_status === s ? "bg-[#0D786A] text-white border-[#0D786A]" : "bg-white text-slate-600 border-slate-200"}`}>
                  {RECON_META[s].label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Variance is shown as a figure only. How you interpret it is up to you.</p>
          </div>

          <div>
            <Label className="text-slate-700 text-sm">Reconciliation notes</Label>
            <Textarea data-testid="report-recon-notes" value={notesVal} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. checked against remittance advice" className="mt-1.5 rounded-xl bg-white" />
            <Button data-testid="report-recon-notes-save" variant="outline" onClick={saveNotes} disabled={busy || notes === null}
              className="mt-2 h-10 rounded-xl">Save notes</Button>
          </div>
        </div>

        {r.notes && (
          <div className="df-card p-4 text-sm" data-testid="report-detail-notes">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Report notes</div>
            <p className="text-slate-700 whitespace-pre-wrap">{r.notes}</p>
          </div>
        )}
      </div>

      {/* Update actual dialog */}
      <Dialog open={actualOpen} onOpenChange={setActualOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Actual payment</DialogTitle>
            <DialogDescription>Enter the amount you actually received. Variance recalculates against the expected payment.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-700">Amount received</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
                <Input type="number" step="0.01" inputMode="decimal" data-testid="report-actual-input" value={actualVal}
                  onChange={(e) => setActualVal(e.target.value)} placeholder="0.00" className="h-12 rounded-xl bg-white pl-7 font-num" />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Payment date</Label>
              <Input type="date" data-testid="report-actual-date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActualOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="report-actual-save" onClick={saveActual} disabled={busy} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid dialog */}
      <Dialog open={paidOpen} onOpenChange={setPaidOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>Confirm the amount received and the payment date. We've pre-filled the expected amount and today's date — change them if the actual payment differs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-slate-700">Amount received</Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-num">$</span>
                <Input type="number" step="0.01" inputMode="decimal" data-testid="mark-paid-amount" value={actualVal}
                  onChange={(e) => setActualVal(e.target.value)} className="h-12 rounded-xl bg-white pl-7 font-num" />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Payment date</Label>
              <Input type="date" data-testid="mark-paid-date" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="mt-1.5 h-12 rounded-xl bg-white" />
            </div>
            <p className="text-[11px] text-slate-400">Reconciliation updates automatically: “Matched” when the amount equals the expected payment, otherwise “Difference Detected” with the variance shown. You decide the reason during reconciliation.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaidOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="mark-paid-confirm" onClick={confirmPaid} disabled={busy} className="rounded-xl bg-[#0D786A] hover:bg-[#0A6055]">Confirm paid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark as Unpaid dialog */}
      <Dialog open={unpaidOpen} onOpenChange={setUnpaidOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Mark as Unpaid?</DialogTitle>
            <DialogDescription>This clears the recorded actual payment, payment date and reconciliation status back to “Awaiting Payment”, and removes reconciliation notes. Your report figures and report notes are kept. This affects received-income totals.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnpaidOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="mark-unpaid-confirm" onClick={confirmUnpaid} disabled={busy} className="rounded-xl bg-amber-600 hover:bg-amber-700">Mark unpaid</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete payment report?</DialogTitle>
            <DialogDescription>This report will be removed from your lists, dashboard and reporting totals. You can restore it later from “Recently deleted”.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="report-delete-confirm" onClick={doDelete} disabled={busy} className="rounded-xl bg-red-600 hover:bg-red-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
